import { useState } from 'react';
import { roleHasPermission } from '@gr/core/accounts/workspaces/permissions';
import { taskManagePermission, type TaskDto } from '@gr/core/platform/tasks/dto';
import type { OperationResult } from '@gr/utils/operation-result';
import { useServices, useT } from '../../../app/di';
import { useConfirm } from '../../../ui-kit/ConfirmDialog/ConfirmDialog';
import { taskKindKey } from './model/task-view';

export interface TaskActions {
  /** TASK-RULE-3: может ли пользователь отменять и возобновлять задачу. */
  canManage(task: TaskDto): boolean;
  pendingId: string | null;
  error: readonly string[] | null;
  cancel(task: TaskDto): Promise<void>;
  resume(task: TaskDto): Promise<void>;
}

/**
 * TASK-UC-4, TASK-UC-5, TASK-UC-6: отмена (с подтверждением) и возобновление. Участник блога с
 * правом вида действует маршрутами блога, администратор вне блога — своими; ответ попадает в
 * хранилище задач вкладки.
 */
export function useTaskActions(): TaskActions {
  const { api, session, tasks } = useServices();
  const { t } = useT();
  const confirm = useConfirm();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<readonly string[] | null>(null);

  const viaBlog = (task: TaskDto): boolean => {
    const permission = taskManagePermission(task.kind);
    const role = task.blogId ? session.state.get().me?.blogs.find((blog) => blog.id === task.blogId)?.role : undefined;
    return permission !== null && role !== undefined && roleHasPermission(role, permission);
  };

  const run = async (task: TaskDto, action: () => Promise<OperationResult<TaskDto>>): Promise<void> => {
    setPendingId(task.id);
    setError(null);
    try {
      const result = await action();
      if (result.success) tasks.merge([result.data]);
      else setError(result.messages);
    } finally {
      setPendingId(null);
    }
  };

  return {
    canManage: (task) => viaBlog(task) || session.isAdmin(),
    pendingId,
    error,
    cancel: async (task) => {
      const title = t(`tasks.kinds.${taskKindKey(task.kind)}`);
      const confirmed = await confirm({ title: t('tasks.card.cancelConfirm', { title }), text: t('tasks.card.cancelText'), confirmLabel: t('tasks.card.cancel'), danger: true });
      if (!confirmed) return;
      await run(task, () => (viaBlog(task) && task.blogId ? api.tasks.cancel(task.blogId, task.id) : api.tasks.cancelAsAdmin(task.id)));
    },
    resume: (task) => run(task, () => (viaBlog(task) && task.blogId ? api.tasks.resume(task.blogId, task.id) : api.tasks.resumeAsAdmin(task.id))),
  };
}
